/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentControlChain.module.scss';

type Translate = (key: string) => string;

const FACT_ICONS = {
  registry: <path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M5 19h14M12 3l8 5H4z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 4 5.3 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.3-4-8.5s1.4-6.2 4-8.5z" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3.5 21 8l-9 4.5L3 8z" />
      <path d="M3 13l9 4.5 9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
} as const;

type Fact = {
  value: number;
  suffix: string;
  label: string;
  icon: keyof typeof FACT_ICONS;
};

const getFacts = (t: Translate): Fact[] => [
  { value: 400, suffix: '+', label: t('Government registries read directly'), icon: 'registry' },
  { value: 200, suffix: '+', label: t('Countries and territories'), icon: 'globe' },
  { value: 600, suffix: 'M+', label: t('Company records, daily-synced'), icon: 'layers' },
  { value: 12, suffix: '', label: t('Years collecting registry data'), icon: 'clock' },
];

const FACT_COUNT = 4;

const PERCENTS = [76, 82, 100];
const MAX_STEP = 8;
const STEP_MS = 260;
const OUTPUT_DELAY = (MAX_STEP + 1) * STEP_MS + 300;

const EFFECTIVE_CONTROL = PERCENTS.reduce((acc, p) => acc * (p / 100), 1) * 100;

const flag = (code: string) => `/storage/flags/${code}.svg`;

export const GovernmentControlChain = () => {
  const { t } = useInt();
  const facts = getFacts(t);
  const treeRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(treeRef, { threshold: 0.15 });
  const [playKey, setPlayKey] = useState(0);
  const [step, setStep] = useState(-1);
  const [output, setOutput] = useState(false);

  const factsRef = useRef<HTMLDivElement>(null);
  const factsRevealed = useIntersection(factsRef, { threshold: 0.3 });
  const [counts, setCounts] = useState(() => Array<number>(FACT_COUNT).fill(0));

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    setStep(-1);
    setOutput(false);

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i <= MAX_STEP; i += 1) {
      timers.push(setTimeout(() => setStep(i), i * STEP_MS));
    }
    timers.push(setTimeout(() => setOutput(true), OUTPUT_DELAY));

    return () => timers.forEach(clearTimeout);
  }, [revealed, playKey]);

  useEffect(() => {
    if (!factsRevealed) {
      return undefined;
    }

    const steps = 24;
    let tick = 0;
    const id = setInterval(() => {
      tick += 1;
      const progress = Math.min(tick / steps, 1);
      setCounts(facts.map((fact) => Math.round(fact.value * progress)));
      if (progress >= 1) {
        clearInterval(id);
      }
    }, 26);

    return () => clearInterval(id);
  }, [factsRevealed]);

  const on = (n: number) => step >= n;

  return (
    <Section background="#081D3D" className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            title={t(
              'A supplier incorporates domestically and screens clean. Three filings out, it is state-controlled.',
            )}
            description={t(
              'Nothing about the bidder changed. What changed is how far the record goes. Each layer below was read from a filing in the register where that company is incorporated — sourced, dated, and traceable back to the document it came from.',
            )}
            theme="dark"
            align="left"
          />

          <div className={cn(styles.facts, factsRevealed && styles.factsOn)} ref={factsRef}>
            {facts.map((fact, i) => (
              <div className={styles.fact} style={{ '--d': `${i * 80}ms` } as CSSProperties} key={fact.label}>
                <span className={styles.factIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {FACT_ICONS[fact.icon]}
                  </svg>
                </span>
                <b className={styles.factValue}>
                  {counts[i]}
                  <em>{fact.suffix}</em>
                </b>
                <span className={styles.factLabel}>{fact.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.treeWrap} ref={treeRef}>
          <div className={styles.tree} key={playKey}>
            <div className={styles.treeHead}>
              <span className={styles.treeLabel}>{t('Group structure · cross-border')}</span>
              <span className={styles.badge}>{t('Illustrative')}</span>
            </div>

            <div className={styles.treeBar}>
              <i style={{ width: `${(Math.max(step, 0) / MAX_STEP) * 100}%` }} />
            </div>

            <div className={styles.treeBody}>
              <svg
                viewBox="0 0 660 496"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Cross-border group structure: a third-country state authority owns the UK bidder through Luxembourg and Netherlands holding companies, and the bidder owns two subsidiaries"
              >
                <defs>
                  <marker
                    id="gov-tar"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                  >
                    <path d="M0 0 L10 5 L0 10z" fill="#6B8FFF" />
                  </marker>
                </defs>

                <g className={cn(styles.tn, on(0) && styles.on)}>
                  <rect
                    x="162"
                    y="18"
                    width="336"
                    height="54"
                    rx="10"
                    fill="rgba(255,122,107,.10)"
                    stroke="#FF7A6B"
                    strokeWidth="1.4"
                  />
                  <rect
                    x="176"
                    y="29"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <text
                    x="191"
                    y="43.5"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    fill="rgba(255,255,255,.65)"
                  >
                    XX
                  </text>
                  <text x="216" y="41" fontFamily="'Inter',sans-serif" fontSize="13.5" fontWeight="700" fill="#fff">
                    SAMPLE STATE INVESTMENT AUTHORITY
                  </text>
                  <text x="216" y="59" fontFamily="'IBM Plex Mono',monospace" fontSize="10" fill="rgba(255,255,255,.5)">
                    Third country · state-owned parent
                  </text>
                </g>

                <path
                  className={cn(styles.te, on(1) && styles.on)}
                  d="M330 72 V 110"
                  fill="none"
                  stroke="#FF7A6B"
                  strokeWidth="1.6"
                  markerEnd="url(#gov-tar)"
                />
                <g className={cn(styles.tn, on(1) && styles.on)}>
                  <rect x="282" y="81" width="97" height="20" rx="5" fill="#0E1526" stroke="rgba(255,255,255,.16)" />
                  <text
                    x="330"
                    y="95"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="11"
                    fill="#fff"
                  >
                    owns 76.00%
                  </text>
                </g>

                <g className={cn(styles.tn, on(2) && styles.on)}>
                  <rect
                    x="162"
                    y="110"
                    width="336"
                    height="54"
                    rx="10"
                    fill="rgba(255,255,255,.05)"
                    stroke="rgba(255,255,255,.3)"
                    strokeWidth="1.2"
                  />
                  <rect
                    x="176"
                    y="121"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <image href={flag('lu')} x="178" y="123" width="26" height="17" />
                  <text x="216" y="133" fontFamily="'Inter',sans-serif" fontSize="13.5" fontWeight="700" fill="#fff">
                    SAMPLE CAPITAL S.à r.l.
                  </text>
                  <text
                    x="216"
                    y="151"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    fill="rgba(255,255,255,.5)"
                  >
                    RCS Luxembourg · majority shareholder
                  </text>
                </g>

                <path
                  className={cn(styles.te, on(3) && styles.on)}
                  d="M330 164 V 202"
                  fill="none"
                  stroke="#6B8FFF"
                  strokeWidth="1.6"
                  markerEnd="url(#gov-tar)"
                />
                <g className={cn(styles.tn, on(3) && styles.on)}>
                  <rect x="282" y="173" width="97" height="20" rx="5" fill="#0E1526" stroke="rgba(255,255,255,.16)" />
                  <text
                    x="330"
                    y="187"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="11"
                    fill="#fff"
                  >
                    owns 82.00%
                  </text>
                </g>

                <g className={cn(styles.tn, on(4) && styles.on)}>
                  <rect
                    x="162"
                    y="202"
                    width="336"
                    height="54"
                    rx="10"
                    fill="rgba(255,255,255,.05)"
                    stroke="rgba(255,255,255,.3)"
                    strokeWidth="1.2"
                  />
                  <rect
                    x="176"
                    y="213"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <image href={flag('nl')} x="178" y="215" width="26" height="17" />
                  <text x="216" y="225" fontFamily="'Inter',sans-serif" fontSize="13.5" fontWeight="700" fill="#fff">
                    SAMPLE HOLDINGS B.V.
                  </text>
                  <text
                    x="216"
                    y="243"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    fill="rgba(255,255,255,.5)"
                  >
                    KVK Handelsregister · sole shareholder
                  </text>
                </g>

                <path
                  className={cn(styles.te, on(5) && styles.on)}
                  d="M330 256 V 308"
                  fill="none"
                  stroke="#6B8FFF"
                  strokeWidth="1.6"
                  markerEnd="url(#gov-tar)"
                />
                <g className={cn(styles.tn, on(5) && styles.on)}>
                  <rect x="278" y="270" width="104" height="20" rx="5" fill="#0E1526" stroke="rgba(255,255,255,.16)" />
                  <text
                    x="330"
                    y="284"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="11"
                    fill="#fff"
                  >
                    owns 100.00%
                  </text>
                </g>

                <g className={cn(styles.tn, on(6) && styles.on)}>
                  <path d="M16 316 H 644" stroke="#9BB2E8" strokeWidth="1.3" strokeDasharray="5 5" fill="none" />
                  <rect x="218" y="305" width="224" height="21" rx="5" fill="#0E1526" />
                  <text
                    x="330"
                    y="320"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    letterSpacing="1.3"
                    fill="#9BB2E8"
                  >
                    YOUR REGISTER STARTS HERE
                  </text>
                </g>
                <path
                  className={cn(styles.te, on(6) && styles.on)}
                  d="M330 316 V 338"
                  fill="none"
                  stroke="#6B8FFF"
                  strokeWidth="1.6"
                  markerEnd="url(#gov-tar)"
                />

                <g className={cn(styles.tn, on(7) && styles.on)}>
                  <rect
                    x="146"
                    y="338"
                    width="368"
                    height="60"
                    rx="10"
                    fill="rgba(107,143,255,.10)"
                    stroke="#6B8FFF"
                    strokeWidth="1.8"
                  />
                  <rect
                    x="160"
                    y="349"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <image href={flag('gb')} x="162" y="351" width="26" height="17" />
                  <text x="200" y="361" fontFamily="'Inter',sans-serif" fontSize="13" fontWeight="700" fill="#fff">
                    SAMPLE INFRASTRUCTURE (UK) LTD
                  </text>
                  <text
                    x="200"
                    y="379"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    fill="rgba(255,255,255,.5)"
                  >
                    Companies House · Active · the bidder
                  </text>
                  <rect x="444" y="349" width="58" height="19" rx="5" fill="rgba(107,143,255,.16)" stroke="#6B8FFF" />
                  <text
                    x="473"
                    y="362.5"
                    textAnchor="middle"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="9.5"
                    letterSpacing="1"
                    fill="#9BB2E8"
                  >
                    BIDDER
                  </text>
                </g>

                <path
                  className={cn(styles.te, on(8) && styles.on)}
                  d="M330 398 V 416 H 160 V 434"
                  fill="none"
                  stroke="#6B8FFF"
                  strokeWidth="1.3"
                  markerEnd="url(#gov-tar)"
                />
                <path
                  className={cn(styles.te, on(8) && styles.on)}
                  d="M330 398 V 416 H 500 V 434"
                  fill="none"
                  stroke="#6B8FFF"
                  strokeWidth="1.3"
                  markerEnd="url(#gov-tar)"
                />

                <g className={cn(styles.tn, on(8) && styles.on)}>
                  <rect
                    x="42"
                    y="434"
                    width="236"
                    height="50"
                    rx="10"
                    fill="rgba(255,255,255,.04)"
                    stroke="rgba(255,255,255,.26)"
                    strokeWidth="1.1"
                  />
                  <rect
                    x="56"
                    y="445"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <image href={flag('ie')} x="58" y="447" width="26" height="17" />
                  <text x="96" y="457" fontFamily="'Inter',sans-serif" fontSize="13.5" fontWeight="700" fill="#fff">
                    SAMPLE MARINE (IE)
                  </text>
                  <text x="96" y="475" fontFamily="'IBM Plex Mono',monospace" fontSize="10" fill="rgba(255,255,255,.5)">
                    CRO · 100.00% subsidiary
                  </text>
                </g>
                <g className={cn(styles.tn, on(8) && styles.on)}>
                  <rect
                    x="382"
                    y="434"
                    width="236"
                    height="50"
                    rx="10"
                    fill="rgba(255,255,255,.04)"
                    stroke="rgba(255,255,255,.26)"
                    strokeWidth="1.1"
                  />
                  <rect
                    x="396"
                    y="445"
                    width="30"
                    height="21"
                    rx="4"
                    fill="rgba(255,255,255,.06)"
                    stroke="rgba(255,255,255,.26)"
                  />
                  <image href={flag('lu')} x="398" y="447" width="26" height="17" />
                  <text x="436" y="457" fontFamily="'Inter',sans-serif" fontSize="13.5" fontWeight="700" fill="#fff">
                    SAMPLE PORT SERVICES
                  </text>
                  <text
                    x="436"
                    y="475"
                    fontFamily="'IBM Plex Mono',monospace"
                    fontSize="10"
                    fill="rgba(255,255,255,.5)"
                  >
                    RCS · 100.00% subsidiary
                  </text>
                </g>
              </svg>
            </div>

            <div className={cn(styles.treeOut, output && styles.treeOutOn)}>
              <span className={styles.outK}>
                {t('Multiply the chain and the third country')} <b>{t('effectively controls the bidder')}</b>
              </span>
              <span className={styles.outV}>
                {EFFECTIVE_CONTROL.toFixed(2)}
                <em>%</em>
              </span>
            </div>
            <div className={cn(styles.treeCalc, output && styles.treeCalcOn)}>
              {PERCENTS.map((p) => p.toFixed(2)).join(' × ')} · 4 registers · 4 jurisdictions · retrieved 2026-07-27
            </div>
          </div>

          <button type="button" className={styles.replay} onClick={() => setPlayKey((key) => key + 1)}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" strokeLinecap="round" />
              <path d="M13.5 2.6v3.6h-3.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('Replay')}
          </button>
        </div>
      </div>
    </Section>
  );
};
